import React, { PropsWithChildren, useState, useMemo } from "react";
import moment from "moment";
import copy from "copy-to-clipboard";
import toast, { Toaster } from "react-hot-toast";
import ReactAudioPlayer from 'react-audio-player';
import { useSelector } from "react-redux";
import { Copy, Hash, Clock, User, VolumeX, Volume2 } from "lucide-react";
import logger from "../utils/logger";

interface leadNoteItemI {
  activity: any;
  key: any;
}

const LeadNoteItems: React.FC<PropsWithChildren<leadNoteItemI>> = ({
  activity,
  key,
}) => {
  // Console log for debugging
  logger.log('LeadNoteItems rendering with activity:', activity);
  
  // Access Redux store to get bucketUrl
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl;

  // State for enhanced features
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullTimestamp, setShowFullTimestamp] = useState(false);

  // Memoized computed values for better performance
  const full_name = useMemo(() => {
    if (!activity?.createdBy) return "Unknown User";
    
    if (typeof activity.createdBy === 'string') {
      return activity.createdBy;
    }
    
    const firstName = activity.createdBy?.firstName || "";
    const lastName = activity.createdBy?.lastName || "";
    
    return firstName && lastName 
      ? `${firstName} ${lastName}` 
      : firstName || lastName || "Unknown User";
  }, [activity?.createdBy]);

  const timeFormat = useMemo(() => {
    if (!activity?.createdAt) return new Date();
    return moment.utc(activity.createdAt).toDate();
  }, [activity?.createdAt]);

  const formattedDate = useMemo(() => {
    if (!activity?.createdAt) return "No date";
    return moment(activity.createdAt).format("DD MMM YYYY");
  }, [activity?.createdAt]);

  const formattedTime = useMemo(() => {
    return moment(timeFormat).format("hh:mm A");
  }, [timeFormat]);

  const relativeTime = useMemo(() => {
    if (!activity?.createdAt) return "Unknown time";
    return moment(activity.createdAt).fromNow();
  }, [activity?.createdAt]);

  // Enhanced copy functionality
  const copyData = () => {
    logger.log('Copying note data to clipboard');
    const textToCopy = activity?.description || "No content to copy";
    copy(textToCopy);
    toast.success("Notes Copied to Clipboard");
  };

  // Parse tags from description (looking for #hashtags)
  const extractedTags = useMemo(() => {
    if (!activity?.description) return [];
    
    const tagRegex = /#(\w+)/g;
    const matches = [];
    let match;
    
    while ((match = tagRegex.exec(activity.description)) !== null) {
      matches.push(match[1]);
    }
    
    return [...new Set(matches)]; // Remove duplicates
  }, [activity?.description]);

  // Enhanced description with clickable tags
  const renderDescription = () => {
    if (!activity?.description) return null;
    
    let description = activity.description;
    
    // Replace hashtags with styled spans
    extractedTags.forEach(tag => {
      const regex = new RegExp(`#${tag}`, 'g');
      description = description.replace(regex, `<span class="note-tag">#${tag}</span>`);
    });
    
    return (
      <div 
        className="note-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  };

  // Check if content is long and needs truncation
  const isLongContent = activity?.description && activity.description.length > 150;
  const displayDescription = isExpanded || !isLongContent 
    ? activity?.description 
    : `${activity?.description?.substring(0, 150)}...`;

  // Define styles as a CSS string
  const styles = `
    .note-container {
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
      border: 1px solid #e5e7eb;
      transition: all 0.2s ease;
      position: relative;
    }
    
    .note-container:hover {
      border-color: #d1d5db;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    
    .note-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
    }
    
    .note-content {
      flex: 1;
    }
    
    .note-description {
      color: #374151;
      font-size: 14px;
      line-height: 1.5;
      margin-bottom: 8px;
      word-wrap: break-word;
    }
    
    .note-description .note-tag {
      background: #dbeafe;
      color: #1d4ed8;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 500;
      font-size: 12px;
      margin: 0 2px;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    
    .note-description .note-tag:hover {
      background: #bfdbfe;
    }
    
    .note-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 12px;
    }
    
    .action-btn {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .action-btn:hover {
      background: #f3f4f6;
      border-color: #d1d5db;
    }
    
    .copy-btn {
      color: #6b7280;
    }
    
    .copy-btn:hover {
      color: #374151;
    }
    
    .note-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #f3f4f6;
    }
    
    .note-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #6b7280;
      font-size: 12px;
    }
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .expand-btn {
      background: none;
      border: none;
      color: #3b82f6;
      cursor: pointer;
      font-size: 12px;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .expand-btn:hover {
      background: #eff6ff;
    }
    
    .audio-container {
      background: #f8fafc;
      border-radius: 8px;
      padding: 12px;
      margin: 8px 0;
    }
    
    .lead-audio-player {
      width: 100%;
      height: 40px;
    }
    
    .tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;
    }
    
    .tag-chip {
      background: #eff6ff;
      color: #1d4ed8;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      <div className="note-container">
        <div className="note-header">
          <div className="note-content">
            {/* Enhanced description with formatting */}
            {activity?.description && (
              <div>
                <div className="note-description">
                  {isLongContent && !isExpanded ? (
                    <>
                      {displayDescription}
                      <button 
                        className="expand-btn"
                        onClick={() => setIsExpanded(true)}
                      >
                        Show more
                      </button>
                    </>
                  ) : (
                    <>
                      {renderDescription()}
                      {isLongContent && isExpanded && (
                        <button 
                          className="expand-btn"
                          onClick={() => setIsExpanded(false)}
                        >
                          Show less
                        </button>
                      )}
                    </>
                  )}
                </div>
                
                {/* Tags extracted from description */}
                {extractedTags.length > 0 && (
                  <div className="tags-container">
                    {extractedTags.map((tag, index) => (
                      <span key={index} className="tag-chip">
                        <Hash size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Enhanced voice note player */}
            {activity?.voiceNote && (
              <div className="audio-container">
                <ReactAudioPlayer
                  src={`${bucketUrl}${activity.voiceNote}`}
                  controls
                  className="lead-audio-player"
                  preload="metadata"
                />
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="note-actions">
            {activity?.description && (
              <button 
                className="action-btn copy-btn"
                onClick={copyData}
                title="Copy note"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Enhanced footer with better meta information */}
        <div className="note-footer">
          <div className="note-meta">
            <div className="meta-item">
              <Clock size={12} />
              <span 
                onClick={() => setShowFullTimestamp(!showFullTimestamp)}
                style={{ cursor: 'pointer' }}
                title="Click to toggle timestamp format"
              >
                {showFullTimestamp 
                  ? `${formattedDate} at ${formattedTime}` 
                  : relativeTime
                }
              </span>
            </div>
            
            <div className="meta-item">
              <User size={12} />
              <span>{full_name}</span>
            </div>
          </div>
        </div>
      </div>
      
      <Toaster 
        position="top-right" 
        reverseOrder={false}
        toastOptions={{
          duration: 2000,
        }}
      />
    </>
  );
};

export default LeadNoteItems;